import React from "react"

import { Button } from "../../../../components/shared/Buttons"
import { Input, TextArea } from "../../../../components/shared/Inputs"
import { Box } from "../../../../components/shared/Layout"
import Typography from "../../../../components/shared/Typography"
import AboutEditor from "../editors/AboutEditor"
import ContactEditor from "../editors/ContactEditor"
import ContentEditor from "../editors/ContentEditor"
import CredentialsEditor from "../editors/CredentialsEditor"
import CTABannerEditor from "../editors/CTABannerEditor"
import CTAEditor from "../editors/CTAEditor"
import EventsEditor from "../editors/EventsEditor"
import FaqEditor from "../editors/FaqEditor"
import FeaturedServicesEditor from "../editors/FeaturedServicesEditor"
import GalleryEditor from "../editors/GalleryEditor"
import HeroEditor from "../editors/HeroEditor"
import KeywordsEditor from "../editors/KeywordsEditor"
import PackagesEditor from "../editors/PackagesEditor"
import QuoteEditor from "../editors/QuoteEditor"
import ReviewsEditor from "../editors/ReviewsEditor"
import ServicesEditor from "../editors/ServicesEditor"
import SubHeroEditor from "../editors/SubHeroEditor"
import ConfirmSaveModal from "../shared/ConfirmSaveModal"

// Generic editor for section types that don't have specialized editors yet
const GenericEditor = ({ section, onSave, onCancel, isEditing = false, sectionType }) => {
  const [formData, setFormData] = React.useState({
    headline: section?.headline || "",
    subheadline: section?.subheadline || "",
    body: section?.body || "",
    imageUrl: section?.imageUrl || "",
    settings: section?.settings || {}
  })

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave({
      layout: sectionType,
      ...formData
    })
  }

  const sectionTypeNames = {
    cta_content: "CTA Content",
    cta_banner: "CTA Banner",
    keywords: "Keywords"
  }

  return (
    <Box className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <Typography variant="h4" className="text-xl font-semibold">
          {isEditing
            ? `${sectionTypeNames[sectionType] || sectionType}`
            : `Add ${sectionTypeNames[sectionType] || sectionType}`}
        </Typography>
        <Typography variant="body" className="mt-1 text-gray-600">
          Configure your {sectionTypeNames[sectionType]?.toLowerCase() || sectionType} section
        </Typography>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Headline"
          value={formData.headline}
          onChange={(e) => handleChange("headline", e.target.value)}
          placeholder="Enter headline"
        />

        <Input
          label="Subheadline"
          value={formData.subheadline}
          onChange={(e) => handleChange("subheadline", e.target.value)}
          placeholder="Enter subheadline"
        />

        <TextArea
          label="Content"
          value={formData.body}
          onChange={(e) => handleChange("body", e.target.value)}
          placeholder="Enter content"
          rows={4}
        />

        <Input
          label="Image URL"
          value={formData.imageUrl}
          onChange={(e) => handleChange("imageUrl", e.target.value)}
          placeholder="https://example.com/image.jpg"
        />

        <div className="flex justify-end gap-3 border-t border-gray-200 pt-6">
          <Button type="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" className="bg-teal-600 text-white hover:bg-teal-700">
            {isEditing
              ? `Update ${sectionTypeNames[sectionType] || sectionType}`
              : `Add ${sectionTypeNames[sectionType] || sectionType}`}
          </Button>
        </div>
      </form>
    </Box>
  )
}

const SectionEditForm = ({
  section,
  sectionType,
  practice,
  onSave,
  onCancel,
  onDelete: handleDeleteFromParent,
  isEditing = false
}) => {
  const [showDeleteModal, setShowDeleteModal] = React.useState(false)

  const handleDeleteRequest = () => {
    if (handleDeleteFromParent) {
      setShowDeleteModal(true)
    }
  }

  const confirmDelete = () => {
    setShowDeleteModal(false)
    if (handleDeleteFromParent) {
      handleDeleteFromParent()
    }
  }

  const commonProps = {
    section,
    practice,
    onSave,
    onCancel,
    onDelete: handleDeleteFromParent ? handleDeleteRequest : undefined,
    isEditing
  }

  const renderEditor = () => {
    switch (sectionType) {
      case "hero":
        return <HeroEditor {...commonProps} />
      case "sub_hero":
        return <SubHeroEditor {...commonProps} />
      case "services":
        return <ServicesEditor {...commonProps} />
      case "featured_services":
        return <FeaturedServicesEditor {...commonProps} />
      case "gallery":
        return <GalleryEditor {...commonProps} />
      case "reviews":
        return <ReviewsEditor {...commonProps} />
      case "content":
        return <ContentEditor {...commonProps} />
      case "quote":
        return <QuoteEditor {...commonProps} />
      case "faq":
        return <FaqEditor {...commonProps} />
      case "about":
        return <AboutEditor {...commonProps} />
      case "contact":
        return <ContactEditor {...commonProps} />
      case "cta_content":
        return <CTAEditor {...commonProps} />
      case "packages":
        return <PackagesEditor {...commonProps} />
      case "events":
        return <EventsEditor {...commonProps} />
      case "credentials":
        return <CredentialsEditor {...commonProps} />
      case "cta_banner":
        return <CTABannerEditor {...commonProps} />
      case "keywords":
        return <KeywordsEditor {...commonProps} />
      default:
        return <GenericEditor {...commonProps} sectionType={sectionType} />
    }
  }

  const editorContent = renderEditor()

  return (
    <>
      {editorContent}
      {handleDeleteFromParent && (
        <ConfirmSaveModal
          visible={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
          title="Delete this section?"
          message="This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
        />
      )}
    </>
  )
}

export default SectionEditForm
